import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import { Grid, GridCellProps, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { CreateReferralMaterialCategory } from 'api/ClientArea/CreateReferralMaterial'
import { ReferralMaterialsCategoriesDto } from 'api/ClientArea/ReferralMaterialsDto'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState } from 'features/DataGrid'
import { useGridStatusState } from 'features/DataGrid/hooks/useGridStatusState'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { getNotificationBaseUrlTypes } from 'store/clientarea/clientarea.actions'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { GridColumn as GridColumnProps } from '../campaignsColumn'
import { referralMaterialsCategoriesColumn } from './referralMaterialsCategoriesColumns'
import { getReferralMaterialsCategoriesFilterFields } from './referralMaterialsCategoriesFilters'
import { ReferralMaterialsCategoryForm } from './referralMaterialsCategoryForm'

const useMapStateToProps = () => ({
  languages: useAppSelector((state) => state.clients.language),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  baseUrlTypes: useAppSelector((state) => state.clientArea.baseUrlTypes),
  referralMaterialsCategoriesByEntity: useAppSelector(
    (state) => state.clientArea.referralMaterialsCategoriesByEntity
  ),
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  hasFilters: useAppSelector(selectGridFilters),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const clientsApiClient = useApiClient(ClientsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchClientLanguages: async () => dispatch(fetchClientLanguagesAction(clientsApiClient)),
    fetchBaseUrlTypes: async () => dispatch(getNotificationBaseUrlTypes(clientAreaApiClient)),
  }
}

const useReferralMaterialsCategoriesFetch = () => {
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [referralMaterialsCategories, setReferralMaterialsCategories] = useState<
    ReferralMaterialsCategoriesDto[]
  >([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { languages, tickmillCompanies, baseUrlTypes, filters, filterTypes } = useMapStateToProps()
  const { fetchFilterTypes, fetchFilters, fetchClientLanguages, fetchBaseUrlTypes } =
    useMapDispatchToProps()

  useEffect(() => {
    fetchReferralMaterialsCategoriesFetch()

    if (isEmpty(filterTypes)) {
      fetchFilterTypes()
    }
    if (isEmpty(filters)) {
      fetchFilters()
    }
    if (isEmpty(languages)) {
      fetchClientLanguages()
    }
    if (isEmpty(baseUrlTypes)) {
      fetchBaseUrlTypes()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReferralMaterialsCategoriesFetch = async () => {
    await fetchGrid(
      async (
        gridFilterState: GridFilterActionState
      ): Promise<PageData<ReferralMaterialsCategoriesDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter)
        const sortQuery = getSortQuery(gridFilterState.sort)
        const response = await clientAreaApiClient.getReferralMaterialsCategories({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) {
          setReferralMaterialsCategories(response.items)
        }
        setTableData({ ...tableData, meta: response })

        return response
      }
    )
  }

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<ReferralMaterialsCategoriesDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSortQuery = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSortQuery(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await clientAreaApiClient.getReferralMaterialsCategories({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setReferralMaterialsCategories(
          response.items.map((x) => Object.assign({ selected: false }, x))
        )
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // tslint:disable-next-line:max-line-length
    [
      getQuery,
      getSortQuery,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    languages,
    tickmillCompanies,
    baseUrlTypes,
    referralMaterialsCategories,
    gridState,
    gridActions,
    filters,
    filterTypes,
    filterFetch,
    tableData,
    setTableData,
    setReferralMaterialsCategories,
    fetchBaseUrlTypes,
    fetchReferralMaterialsCategoriesFetch,
    setGridFilters: gridActions.setGridFilters,
    gridStatusState,
    gridStatusActions,
    getQuery,
    getSortQuery,
  }
}

const useReferralMaterialsCategoryMutate = () => {
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const setToast = useContext(ToastContext)

  const handleAddSubmit = async (data: CreateReferralMaterialCategory) => {
    await clientAreaApiClient.createReferralMaterialsCategory(data)
    setToast(successToast(`Referral Material Category added successfully.`))
  }

  const handleRemove = async (data: ReferralMaterialsCategoriesDto) => {
    await clientAreaApiClient.deleteReferralMaterialCategory(data.id)
    setToast(successToast(`Referral Material Category removed successfully.`))
  }

  return { handleAddSubmit, handleRemove }
}

const useReferralMaterialsCategoriesHandlers = (
  props: ReturnType<typeof useReferralMaterialsCategoriesFetch>
) => {
  const { gridState, gridActions } = props
  const { getQuery, getSortQuery, setReferralMaterialsCategories, tableData, setTableData } = props

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const handleSkipCountChange = async (skipCount: boolean) => {
    props.gridStatusActions.setFetching(true)
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await clientAreaApiClient.getReferralMaterialsCategories({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    props.gridStatusActions.setFetching(false)

    if (response?.items) {
      setReferralMaterialsCategories(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    props.gridStatusActions.setFetching(true)
    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSortQuery(event.sort)
    const response = await clientAreaApiClient.getReferralMaterialsCategories({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    props.gridStatusActions.setFetching(false)

    if (response?.items) {
      setReferralMaterialsCategories(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    props.gridStatusActions.setFetching(true)
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await clientAreaApiClient.getReferralMaterialsCategories({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    props.gridStatusActions.setFetching(false)
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setReferralMaterialsCategories(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    props.gridStatusActions.setFetching(true)
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await clientAreaApiClient.getReferralMaterialsCategories({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    props.gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total,
    })

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setReferralMaterialsCategories(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return {
    handleSkipCountChange,
    handleSortChange,
    handlePageSizeChange,
    handleGridPageChange,
    filterColumn,
  }
}

interface ModalProps {
  data: ReferralMaterialsCategoriesDto | undefined
  visible: boolean
}

const initialModalState = { data: undefined, visible: false }

const useReferralMaterialCategoryModal = () => {
  const [referralMaterialCategoryModal, setReferralMaterialCategoryModal] =
    useState<ModalProps>(initialModalState)

  const handleReferralMaterialCategoryModalOpen = (data?: ReferralMaterialsCategoriesDto) => {
    setReferralMaterialCategoryModal({ data, visible: true })
  }

  const handleReferralMaterialCategoryModalClose = () => {
    setReferralMaterialCategoryModal(initialModalState)
  }

  return {
    referralMaterialCategoryModal,
    handleReferralMaterialCategoryModalOpen,
    handleReferralMaterialCategoryModalClose,
  }
}

const useConfirmationModal = () => {
  const [confirmationModal, setConfirmationModal] = useState<ModalProps>(initialModalState)

  const handleConfirmationModalOpen = (data?: ReferralMaterialsCategoriesDto) => {
    if (data) {
      setConfirmationModal({ data, visible: true })
    }
  }

  const handleConfirmationModalClose = () => {
    setConfirmationModal(initialModalState)
  }

  return { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose }
}

const ReferralMaterialsCategoriesPage = () => {
  const crumbs = [
    {
      title: 'Marketing - Referral Materials Categories',
      link: links.referralMaterialsCategories.url,
    },
  ]

  const referralMaterialsCategoriesFetch = useReferralMaterialsCategoriesFetch()
  const {
    gridState,
    gridStatusState,
    languages,
    baseUrlTypes,
    tickmillCompanies,
    gridStatusActions,
    gridActions,
    getQuery,
    filterFetch,
  } = referralMaterialsCategoriesFetch
  const { tableData, setTableData, referralMaterialsCategories } = referralMaterialsCategoriesFetch

  const {
    referralMaterialCategoryModal,
    handleReferralMaterialCategoryModalOpen,
    handleReferralMaterialCategoryModalClose,
  } = useReferralMaterialCategoryModal()
  const { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose } =
    useConfirmationModal()

  const handlers = useReferralMaterialsCategoriesHandlers(referralMaterialsCategoriesFetch)
  const mutate = useReferralMaterialsCategoryMutate()
  const { handleSortChange, handleGridPageChange, handlePageSizeChange, handleSkipCountChange } =
    handlers

  const handleSubmit = async (data: CreateReferralMaterialCategory) => {
    await mutate.handleAddSubmit(data)
    await referralMaterialsCategoriesFetch.fetchReferralMaterialsCategoriesFetch()
    handleReferralMaterialCategoryModalClose()
  }

  const handleRemoveReferralMaterialsCategory = async (data?: ReferralMaterialsCategoriesDto) => {
    if (data?.id) {
      await mutate.handleRemove(data)
      await referralMaterialsCategoriesFetch.fetchReferralMaterialsCategoriesFetch()
      handleConfirmationModalClose()
    }
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {confirmationModal.data && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={() => handleRemoveReferralMaterialsCategory(confirmationModal.data)}
          onCancel={handleConfirmationModalClose}
          visible={confirmationModal.visible}
          renderTitle={
            <div>
              <p>Are you sure you want to delete this IB referral material page record?</p>
            </div>
          }
        />
      )}
      {referralMaterialCategoryModal.visible && (
        <Dialog
          open={referralMaterialCategoryModal.visible}
          onOpenChange={() => handleReferralMaterialCategoryModalClose()}
        >
          <ReferralMaterialsCategoryForm
            data={referralMaterialCategoryModal.data}
            languages={languages.filter((l) => l.clientAreaVisible)}
            tickmillCompanies={tickmillCompanies}
            baseUrlTypes={baseUrlTypes}
            onSubmit={handleSubmit}
          />
        </Dialog>
      )}

      <Breadcrumbs crumbs={crumbs} />

      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.ReferralMaterialsCategories}
        filterFields={getReferralMaterialsCategoriesFilterFields()}
        getQuery={getQuery}
      />

      <AddButton text='New category' onClick={handleReferralMaterialCategoryModalOpen} />

      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={referralMaterialsCategories}
          sort={gridState.sort}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          onSortChange={handleSortChange}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          {referralMaterialsCategoriesColumn.filter(filterColumn).map((column, idx) => (
            <GridColumn key={idx + 2} {...column} />
          ))}
          <GridColumn
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Delete',
                      onClick: () => handleConfirmationModalOpen(gridCellProps.dataItem),
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

const filterColumn = (c: GridColumnProps) => {
  return c.show
}

export default ReferralMaterialsCategoriesPage
